import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FrameworkModules from "../components/FrameworkModules";
import PartnerLogos from "../components/PartnerLogos";
import Contributors from "../components/Contributors";
import Newsletter from "../components/Newsletter";
import ContributeApplicationDialog from "../components/ContributeApplicationDialog";
import Endorsements from "../components/Endorsements";
import { Button } from "@/components/ui/button";
import AIFrameworkComparison from "../components/AIFrameworkComparison";
import { navigateToSection } from "../utils/scrollUtils";
import SEOHead from "../components/SEOHead";
const AIMusicFramework: React.FC = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "AIM Framework - AI Music Industry Standards",
    "description": "A comprehensive framework of ontologies and tech systems designed for the Music Industry to ethically include AI within existing systems.",
    "author": {
      "@type": "Organization",
      "name": "TagMusic"
    },
    "publisher": {
      "@type": "Organization",
      "name": "TagMusic",
      "logo": {
        "@type": "ImageObject",
        "url": "https://your-domain.com/lovable-uploads/a9972fbb-fae2-4800-9c1b-9c1e0dd3fe4b.png"
      }
    },
    "datePublished": "2024-08-18",
    "dateModified": "2024-08-18"
  };
  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };
  const handleBenefitsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('stakeholder-benefits')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handleReportClick = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('report-section')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="flex flex-col min-h-screen">
      <SEOHead title="AIM Framework | AI Music Industry Standards & Ontologies - TagMusic" description="A comprehensive framework of ontologies and tech systems designed for the Music Industry to ethically include AI within existing systems. Explore the AIM Framework report and mapping." keywords="AIM framework, AI music standards, music ontologies, AI ethics in music, music industry framework, AI music technology, music AI guidelines" canonical="https://your-domain.com/ai-music-framework" structuredData={structuredData} />
      {/* Dialog for application form */}
      <ContributeApplicationDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-tagblue via-tagblue-light to-tagblue text-white overflow-hidden">
        {/* Subtle 3D Background Elements */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-32 left-10 w-20 h-20 bg-white/10 rounded-2xl backdrop-blur-sm rotate-12 animate-float" />
          <div className="absolute top-20 right-20 w-16 h-16 bg-white/10 rounded-xl backdrop-blur-sm -rotate-12 animate-float" style={{
          animationDelay: '2s'
        }} />
          <div className="absolute bottom-32 left-20 w-12 h-12 bg-white/10 rounded-lg backdrop-blur-sm rotate-45 animate-float" style={{
          animationDelay: '4s'
        }} />
          <div className="absolute bottom-20 right-10 w-16 h-16 bg-white/10 rounded-xl backdrop-blur-sm -rotate-6 animate-float" style={{
          animationDelay: '1s'
        }} />
        </div>
        
        <div className="relative z-50">
          <Navbar />
        </div>
        <div className="container mx-auto px-4 py-16 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 animate-slide-up">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-blue-100 drop-shadow-2xl">
              <span className="font-audiowide text-6xl md:text-7xl">AIM</span> Framework
            </span>
          </h1>
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-4 border border-white/20 shadow-2xl animate-slide-up max-w-3xl mx-auto" style={{
          animationDelay: '0.2s'
        }}>
            <p className="text-white/95 text-lg leading-relaxed">A set of ontologies & tech systems that works for the Music Industry</p>
          </div>
          <p className="text-white/90 text-md mb-8 animate-slide-up" style={{
          animationDelay: '0.3s'
        }}>Envisioned to ethically include AI within existing systems</p>
          
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 animate-slide-up" style={{
          animationDelay: '0.4s'
        }}>
            <Link to="/contact" className="bg-white/15 backdrop-blur-md text-white px-6 py-3 rounded-full text-sm border border-white/30 shadow-xl hover:bg-white/25 transition-all duration-300">Contact us</Link>
            <Button className="bg-white text-tagblue hover:bg-blue-50 px-8 py-6 rounded-full shadow-2xl hover:shadow-white/25 hover:scale-105 transform transition-all duration-300" onClick={handleReportClick}>
              Music focussed mapping
            </Button>
          </div>
        </div>
      </div>
      
      {/* Framework Modules */}
      <FrameworkModules />
      
      {/* AI Framework Comparison - New section */}
      <AIFrameworkComparison />
      
      {/* Report Divider Section - Dark Design like Industry Compliance */}
      <div id="report-section" className="py-16 bg-gray-800 text-white relative overflow-hidden">
        {/* 3D Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-24 h-24 bg-tagblue/20 rounded-3xl backdrop-blur-sm rotate-12 animate-float" />
          <div className="absolute top-20 right-20 w-16 h-16 bg-tagblue-light/20 rounded-xl backdrop-blur-sm -rotate-12 animate-float" style={{
          animationDelay: '2s'
        }} />
          <div className="absolute bottom-20 left-20 w-12 h-12 bg-tagblue/20 rounded-lg backdrop-blur-sm rotate-45 animate-float" style={{
          animationDelay: '4s'
        }} />
          <div className="absolute bottom-10 right-10 w-20 h-20 bg-tagblue-light/20 rounded-xl backdrop-blur-sm -rotate-6 animate-float" style={{
          animationDelay: '1s'
        }} />
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-tagblue to-white">
              Report - <span className="font-audiowide text-6xl md:text-7xl">AIM</span> Framework for Music
            </span>
          </h2>
          <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10 shadow-xl max-w-4xl mx-auto">
            <p className="text-gray-300 text-xl leading-relaxed">
              Mapping the existing AI Music ecosystem with the AIM Framework built by Tag
            </p>
          </div>
        </div>
      </div>
      
      {/* Report Introduction Section - Enhanced with 3D Effects */}
      <div className="py-20 bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-100 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10 bg-contain bg-center bg-no-repeat" />
        
        {/* Enhanced 3D Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-24 h-24 bg-tagblue/10 rounded-full blur-xl animate-float" />
          <div className="absolute bottom-10 left-10 w-32 h-32 bg-tagblue-light/10 rounded-full blur-xl animate-float" style={{
          animationDelay: '3s'
        }} />
          <div className="absolute top-32 left-20 w-20 h-20 bg-tagblue/15 rounded-3xl backdrop-blur-sm rotate-12 animate-float" style={{
          animationDelay: '1s'
        }} />
          <div className="absolute bottom-32 right-20 w-16 h-16 bg-tagblue-light/15 rounded-2xl backdrop-blur-sm -rotate-12 animate-float" style={{
          animationDelay: '2s'
        }} />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto text-center">
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-12">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-tagblue to-gray-900 text-5xl">
                Understanding the AI Music Landscape
              </span>
            </h3>
            
            <div className="grid md:grid-cols-2 gap-8 text-left">
              <div className="group relative">
                {/* Card glow effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-tagblue to-tagblue-light rounded-3xl blur-2xl opacity-25 group-hover:opacity-50 transition-opacity duration-500" />
                
                {/* Main card */}
                <div className="relative bg-white/80 backdrop-blur-md rounded-3xl p-10 shadow-xl hover:shadow-2xl transition-all duration-500 border border-white/50 group-hover:scale-105 transform">
                  <div className="absolute -top-4 -right-4 w-8 h-8 bg-tagblue/20 rounded-xl rotate-45 animate-float opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  <h4 className="text-2xl font-bold text-tagblue mb-6 flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-tagblue to-tagblue-dark rounded-xl flex items-center justify-center shadow-lg">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                        <path d="M3 3v18h18" />
                        <path d="m19 9-5 5-4-4-3 3" />
                      </svg>
                    </div>
                    Current State
                  </h4>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    The AI music ecosystem is rapidly evolving with diverse stakeholders, technologies, and use cases. 
                    This report provides a comprehensive mapping of the current landscape and proposes a unified framework 
                    for responsible AI music development.
                  </p>
                  
                  {/* Decorative bottom accent */}
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-gradient-to-r from-tagblue to-tagblue-light rounded-full opacity-60" />
                </div>
              </div>
              
              <div className="group relative" style={{
              animationDelay: '0.3s'
            }}>
                {/* Card glow effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-tagblue-light to-tagblue rounded-3xl blur-2xl opacity-25 group-hover:opacity-50 transition-opacity duration-500" />
                
                {/* Main card */}
                <div className="relative bg-white/80 backdrop-blur-md rounded-3xl p-10 shadow-xl hover:shadow-2xl transition-all duration-500 border border-white/50 group-hover:scale-105 transform">
                  <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-tagblue-light/30 rounded-lg -rotate-12 animate-float opacity-0 group-hover:opacity-100 transition-opacity" style={{
                  animationDelay: '1s'
                }} />
                  
                  <h4 className="text-2xl font-bold text-tagblue mb-6 flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-tagblue-light to-tagblue rounded-xl flex items-center justify-center shadow-lg">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    </div>
                    Our Vision
                  </h4>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    The AIM Framework establishes standards for transparency, attribution, and ethical practices 
                    in AI music creation. By mapping existing solutions to our framework, we identify gaps and 
                    opportunities for industry collaboration.
                  </p>
                  
                  {/* Decorative bottom accent */}
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-gradient-to-r from-tagblue-light to-tagblue rounded-full opacity-60" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stakeholder Benefits - Hidden for now */}
      {/* 
       <div id="stakeholder-benefits" className="py-16 bg-white relative overflow-hidden">
        ... stakeholder benefits content ...
       </div>
       */}
      
      {/* Partners Section */}
      <div className="py-16 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        {/* 3D Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-24 h-24 bg-tagblue/10 rounded-3xl backdrop-blur-sm rotate-12 animate-float" />
          <div className="absolute bottom-20 right-20 w-20 h-20 bg-tagblue-light/10 rounded-2xl backdrop-blur-sm -rotate-12 animate-float" style={{
          animationDelay: '2s'
        }} />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-3xl font-bold text-center mb-12">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-tagblue to-gray-900 text-5xl">
              Featured Work | Support
            </span>
          </h2>
          <PartnerLogos />
        </div>
      </div>
      
      {/* Endorsements Section - Hidden for now */}
      {/* 
       <div className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Industry Endorsements</h2>
          <Endorsements />
        </div>
       </div>
       */}

      {/* Acknowledgements */}
      <div className="py-16 bg-white relative overflow-hidden">
        {/* 3D Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-28 h-28 bg-tagblue/10 rounded-3xl backdrop-blur-sm rotate-12 animate-float" />
          <div className="absolute bottom-10 left-20 w-16 h-16 bg-tagblue-light/10 rounded-2xl backdrop-blur-sm -rotate-12 animate-float" style={{
          animationDelay: '2s'
        }} />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-3xl font-bold text-center mb-12">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-tagblue to-gray-900 text-5xl">
              Acknowledgements
            </span>
          </h2>
          <Contributors />
        </div>
      </div>

      {/* Newsletter */}
      <Newsletter />

      <Footer />
    </div>;
};
export default AIMusicFramework;